import { useEffect, useState } from "react";
import PageLayout from "../components/PageLayout";
import RoundBox from "../components/RoundBox";
import {
  buttonStyle,
  buttonBackgroundColor,
  GH_COLORS,
  tanstackTableStyle,
  getTanstackThStyle,
  getTanstackTdStyle,
  tanstackTrStyle,
  emptyRowStyle,
} from "../components/Style";
import { apiClient } from "../services/api";

interface AlimtalkTemplate {
  id: number;
  tplCode: string;
  tplName: string;
  tplContent: string;
  createdAt: string;
  updatedAt: string;
}

interface AlimtalkEventSetting {
  eventType: string;
  templateId: number | null;
  template: { id: number; tplCode: string; tplName: string; tplContent: string } | null;
}

const inputBase: React.CSSProperties = {
  width: "100%",
  borderRadius: 6,
  border: `1px solid ${GH_COLORS.border}`,
  padding: "7px 10px",
  fontSize: 14,
  background: "#fff",
  color: GH_COLORS.fgDefault,
  boxSizing: "border-box",
  fontFamily: "inherit",
};

const labelStyle: React.CSSProperties = {
  fontSize: 13,
  fontWeight: 600,
  color: GH_COLORS.fgMuted,
  marginBottom: 4,
  display: "block",
};

const requiredMark: React.CSSProperties = {
  color: "#d73a49",
  marginLeft: 3,
};

/* ───── 출결 알림톡 연결 설정 ───── */
function AttendanceEventSettings({ templates }: { templates: AlimtalkTemplate[] }) {
  const [events, setEvents] = useState<AlimtalkEventSetting[]>([]);
  const [pending, setPending] = useState<Record<string, number | null>>({});
  const [saving, setSaving] = useState<Record<string, boolean>>({});

  const loadEvents = () =>
    apiClient.getAttendanceAlimtalkEvents().then(setEvents).catch(console.error);

  useEffect(() => {
    loadEvents();
  }, []);

  const handleSelect = (eventType: string, value: string) => {
    setPending((prev) => ({ ...prev, [eventType]: value === "" ? null : Number(value) }));
  };

  const handleSave = async (eventType: string) => {
    const templateId = eventType in pending ? pending[eventType] : (events.find((e) => e.eventType === eventType)?.templateId ?? null);
    setSaving((prev) => ({ ...prev, [eventType]: true }));
    try {
      await apiClient.setAttendanceAlimtalkEvent(eventType, templateId);
      await loadEvents();
      setPending((prev) => { const next = { ...prev }; delete next[eventType]; return next; });
    } finally {
      setSaving((prev) => ({ ...prev, [eventType]: false }));
    }
  };

  const EVENT_LABELS: Record<string, string> = { 등원: "등원 🟢", 하원: "하원 🔴" };

  return (
    <RoundBox label="출결 알림톡 연결" style={{ marginBottom: 24 }}>
      <p style={{ fontSize: 13, color: GH_COLORS.fgMuted, marginTop: 0, marginBottom: 12 }}>
        등원·하원 기록 시 발송할 알림톡 템플릿을 연결합니다.
        템플릿 내 <code style={{ fontSize: 12 }}>#{"{"}{"\uD559\uC0DD"}{"}"}</code>·
        <code style={{ fontSize: 12 }}>#{"{"}{"\uC2DC"}{"}"}</code>·
        <code style={{ fontSize: 12 }}>#{"{"}{"\uBD84"}{"}"}</code>
        은 자동으로 치환됩니다.
      </p>
      <table style={{ ...tanstackTableStyle, maxWidth: 640 }}>
        <thead>
          <tr>
            <th style={getTanstackThStyle(false, 100)}>이벤트</th>
            <th style={getTanstackThStyle(false, 360)}>연결 템플릿</th>
            <th style={getTanstackThStyle(false, 80)}>저장</th>
          </tr>
        </thead>
        <tbody>
          {["등원", "하원"].map((et) => {
            const cur = events.find((e) => e.eventType === et);
            const selectedId = et in pending ? pending[et] : (cur?.templateId ?? null);
            return (
              <tr key={et} style={tanstackTrStyle}>
                <td style={{ ...getTanstackTdStyle(false, 100), textAlign: "center", fontWeight: 600 }}>
                  {EVENT_LABELS[et] ?? et}
                </td>
                <td style={{ ...getTanstackTdStyle(false, 360), textAlign: "center" }}>
                  <select
                    value={selectedId ?? ""}
                    onChange={(e) => handleSelect(et, e.target.value)}
                    style={{
                      width: "100%",
                      borderRadius: 6,
                      border: `1px solid ${GH_COLORS.border}`,
                      padding: "6px 8px",
                      fontSize: 13,
                      background: "#fff",
                      color: GH_COLORS.fgDefault,
                      fontFamily: "inherit",
                    }}
                  >
                    <option value="">— 미설정 —</option>
                    {templates.map((t) => (
                      <option key={t.id} value={t.id}>
                        [{t.tplCode}] {t.tplName}
                      </option>
                    ))}
                  </select>
                </td>
                <td style={{ ...getTanstackTdStyle(false, 80), textAlign: "center" }}>
                  <button
                    style={{
                      ...buttonStyle,
                      ...buttonBackgroundColor.save,
                      fontSize: 12,
                      padding: "4px 12px",
                      opacity: saving[et] ? 0.5 : 1,
                    }}
                    disabled={!!saving[et]}
                    onClick={() => handleSave(et)}
                  >
                    {saving[et] ? "저장 중..." : "저장"}
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </RoundBox>
  );
}

/* ───── 편집 폼 ───── */
function TemplateForm({
  initial,
  onSave,
  onCancel,
}: {
  initial?: AlimtalkTemplate;
  onSave: (tplCode: string, tplName: string, tplContent: string) => Promise<void>;
  onCancel: () => void;
}) {
  const [tplCode, setTplCode] = useState(initial?.tplCode ?? "");
  const [tplName, setTplName] = useState(initial?.tplName ?? "");
  const [tplContent, setTplContent] = useState(initial?.tplContent ?? "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const canSave = tplCode.trim() && tplName.trim() && tplContent.trim() && !saving;

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true);
    setError(null);
    try {
      await onSave(tplCode.trim(), tplName.trim(), tplContent.trim());
    } catch (e: any) {
      setError(e?.message ?? "저장 실패");
    } finally {
      setSaving(false);
    }
  };

  return (
    <RoundBox
      label={initial ? "템플릿 수정" : "새 알림톡 템플릿"}
      style={{ marginBottom: 24, maxWidth: 580 }}
    >
      <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        <div>
          <label style={labelStyle}>
            템플릿 코드<span style={requiredMark}>*</span>
          </label>
          <input
            type="text"
            value={tplCode}
            onChange={(e) => setTplCode(e.target.value)}
            placeholder="예: TM_CHECKIN"
            style={inputBase}
          />
        </div>
        <div>
          <label style={labelStyle}>
            템플릿 이름<span style={requiredMark}>*</span>
          </label>
          <input
            type="text"
            value={tplName}
            onChange={(e) => setTplName(e.target.value)}
            placeholder="예: 등원 알림"
            style={inputBase}
          />
        </div>
        <div>
          <label style={labelStyle}>
            템플릿 내용<span style={requiredMark}>*</span>
          </label>
          <textarea
            value={tplContent}
            onChange={(e) => setTplContent(e.target.value)}
            rows={5}
            placeholder={"예: [학원명] #{이름} 학생이 #{시간}에 등원하였습니다."}
            style={{ ...inputBase, resize: "vertical", lineHeight: "1.6" }}
          />
        </div>

        {error && (
          <span style={{ fontSize: 13, color: GH_COLORS.btnDangerBg, fontWeight: 600 }}>
            {error}
          </span>
        )}

        <div style={{ display: "flex", gap: 8 }}>
          <button
            style={{
              ...buttonStyle,
              ...buttonBackgroundColor.save,
              opacity: canSave ? 1 : 0.5,
              cursor: canSave ? "pointer" : "not-allowed",
            }}
            onClick={handleSave}
            disabled={!canSave}
          >
            {saving ? "저장 중..." : "저장"}
          </button>
          <button
            style={{ ...buttonStyle, ...buttonBackgroundColor.cancel }}
            onClick={onCancel}
          >
            취소
          </button>
        </div>
      </div>
    </RoundBox>
  );
}

/* ───── 페이지 ───── */
export default function AlimtalkTemplatePage() {
  const [templates, setTemplates] = useState<AlimtalkTemplate[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [editTarget, setEditTarget] = useState<AlimtalkTemplate | null>(null);

  const load = () =>
    apiClient.getAlimtalkTemplates().then(setTemplates).catch(console.error);

  useEffect(() => {
    load();
  }, []);

  const handleCreate = async (tplCode: string, tplName: string, tplContent: string) => {
    await apiClient.createAlimtalkTemplate(tplCode, tplName, tplContent);
    await load();
    setShowForm(false);
  };

  const handleUpdate = async (tplCode: string, tplName: string, tplContent: string) => {
    if (!editTarget) return;
    await apiClient.updateAlimtalkTemplate(editTarget.id, tplCode, tplName, tplContent);
    await load();
    setEditTarget(null);
  };

  const handleDelete = async (t: AlimtalkTemplate) => {
    if (!window.confirm(`"${t.tplName}" 템플릿을 삭제하시겠습니까?`)) return;
    await apiClient.deleteAlimtalkTemplate(t.id);
    await load();
  };

  const isFormOpen = showForm || editTarget !== null;

  return (
    <PageLayout title="알림톡 템플릿 관리">
      {/* 출결 알림톡 연결 설정 */}
      <AttendanceEventSettings templates={templates} />

      {/* 추가 버튼 */}
      {!isFormOpen && (
        <div style={{ marginBottom: 16 }}>
          <button
            style={{ ...buttonStyle, ...buttonBackgroundColor.save }}
            onClick={() => setShowForm(true)}
          >
            + 알림톡 템플릿 추가
          </button>
        </div>
      )}

      {/* 신규 추가 폼 */}
      {showForm && (
        <TemplateForm
          onSave={handleCreate}
          onCancel={() => setShowForm(false)}
        />
      )}

      {/* 수정 폼 */}
      {editTarget && (
        <TemplateForm
          initial={editTarget}
          onSave={handleUpdate}
          onCancel={() => setEditTarget(null)}
        />
      )}

      {/* 테이블 */}
      <div style={{ overflow: "auto" }}>
        <table style={tanstackTableStyle}>
          <thead>
            <tr>
              <th style={getTanstackThStyle(false, 140)}>템플릿 코드</th>
              <th style={getTanstackThStyle(false, 180)}>템플릿 이름</th>
              <th style={getTanstackThStyle(false, 400)}>템플릿 내용</th>
              <th style={getTanstackThStyle(false, 100)}>관리</th>
            </tr>
          </thead>
          <tbody>
            {templates.length === 0 ? (
              <tr>
                <td colSpan={4} style={emptyRowStyle}>
                  등록된 알림톡 템플릿이 없습니다.
                </td>
              </tr>
            ) : (
              templates.map((t) => (
                <tr key={t.id} style={tanstackTrStyle}>
                  <td style={{ ...getTanstackTdStyle(false, 140), textAlign: "center" }}>
                    <span style={{ fontSize: 13, fontFamily: "monospace" }}>{t.tplCode}</span>
                  </td>
                  <td style={{ ...getTanstackTdStyle(false, 180), textAlign: "center" }}>
                    <span style={{ fontSize: 13 }}>{t.tplName}</span>
                  </td>
                  <td style={{ ...getTanstackTdStyle(false, 400), textAlign: "left", whiteSpace: "pre-wrap", lineHeight: 1.5 }}>
                    <span style={{ fontSize: 13 }}>{t.tplContent}</span>
                  </td>
                  <td style={{ ...getTanstackTdStyle(false, 100), textAlign: "center" }}>
                    <div style={{ display: "flex", gap: 6, justifyContent: "center" }}>
                      <button
                        style={{ ...buttonStyle, ...buttonBackgroundColor.edit, fontSize: 12, padding: "4px 10px" }}
                        onClick={() => { setShowForm(false); setEditTarget(t); }}
                      >
                        수정
                      </button>
                      <button
                        style={{ ...buttonStyle, ...buttonBackgroundColor.delete, fontSize: 12, padding: "4px 10px" }}
                        onClick={() => handleDelete(t)}
                      >
                        삭제
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </PageLayout>
  );
}
